import { CalendarMeta, Era, WeekNote } from '../types';

const TOKEN_KEY = 'lifecal_admin_token';

export interface IApiClient {
  getAuthToken(): string | null;
  setAuthToken(token: string | null): void;
  getMeta(): Promise<CalendarMeta>;
  getWeeks(): Promise<WeekNote[]>;
  login(tokenOrPass: string): Promise<{ success: boolean; message?: string }>;
  verifyAuth(): Promise<boolean>;
  saveEra(era: Era): Promise<Era>;
  deleteEra(id: string): Promise<void>;
  saveNote(note: WeekNote): Promise<WeekNote>;
  deleteNote(id: string): Promise<void>;
  updateConfig(config: {
    birthdate?: string;
    total_years?: number;
    title?: string;
    bio?: string;
    admin_password?: string;
  }): Promise<void>;
  exportBackup(): Promise<unknown>;
  importBackup(data: unknown): Promise<void>;
}

export class WorkerApiClient implements IApiClient {
  private baseUrl: string;

  constructor(baseUrl = '') {
    this.baseUrl = baseUrl;
  }

  getAuthToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  setAuthToken(token: string | null): void {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  }

  private getHeaders(): HeadersInit {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const token = this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  async getMeta(): Promise<CalendarMeta> {
    const res = await fetch(`${this.baseUrl}/api/calendar/meta`, {
      headers: this.getHeaders(),
    });
    if (!res.ok) throw new Error('Failed to fetch calendar metadata');
    return res.json();
  }

  async getWeeks(): Promise<WeekNote[]> {
    const res = await fetch(`${this.baseUrl}/api/calendar/weeks`, {
      headers: this.getHeaders(),
    });
    if (!res.ok) throw new Error('Failed to fetch week notes');
    const data = (await res.json()) as { notes: WeekNote[] };
    return data.notes || [];
  }

  async login(tokenOrPass: string): Promise<{ success: boolean; message?: string }> {
    const res = await fetch(`${this.baseUrl}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: tokenOrPass }),
    });

    if (res.ok) {
      this.setAuthToken(tokenOrPass);
      return { success: true };
    }

    const errorData = (await res.json().catch(() => ({}))) as { message?: string };
    return { success: false, message: errorData.message || 'Authentication failed' };
  }

  async verifyAuth(): Promise<boolean> {
    const token = this.getAuthToken();
    if (!token) return false;

    try {
      const res = await fetch(`${this.baseUrl}/api/auth/verify`, {
        headers: this.getHeaders(),
      });
      if (res.ok) {
        const data = (await res.json()) as { isAdmin: boolean };
        return !!data.isAdmin;
      }
      return false;
    } catch {
      return false;
    }
  }

  async saveEra(era: Era): Promise<Era> {
    const res = await fetch(`${this.baseUrl}/api/eras`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(era),
    });
    if (!res.ok) throw new Error('Failed to save era');
    const data = (await res.json()) as { era: Era };
    return data.era;
  }

  async deleteEra(id: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/api/eras/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    if (!res.ok) throw new Error('Failed to delete era');
  }

  async saveNote(note: WeekNote): Promise<WeekNote> {
    const res = await fetch(`${this.baseUrl}/api/notes`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(note),
    });
    if (!res.ok) throw new Error('Failed to save note');
    const data = (await res.json()) as { note: WeekNote };
    return data.note;
  }

  async deleteNote(id: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/api/notes/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    if (!res.ok) throw new Error('Failed to delete note');
  }

  async updateConfig(config: {
    birthdate?: string;
    total_years?: number;
    title?: string;
    bio?: string;
    admin_password?: string;
  }): Promise<void> {
    const res = await fetch(`${this.baseUrl}/api/config`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(config),
    });
    if (!res.ok) throw new Error('Failed to update config');
  }

  async exportBackup(): Promise<unknown> {
    const res = await fetch(`${this.baseUrl}/api/backup/export`, {
      headers: this.getHeaders(),
    });
    if (!res.ok) throw new Error('Failed to export backup');
    return res.json();
  }

  async importBackup(data: unknown): Promise<void> {
    const res = await fetch(`${this.baseUrl}/api/backup/import`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to restore backup');
  }
}

export const api: IApiClient = new WorkerApiClient(import.meta.env.VITE_API_URL || '');
